import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";

const EMPTY = { title: "", starts_at: "", location: "", seats: "", notes: "", active: true };
const field = "w-full bg-transparent border-b border-[#D9B47A]/30 py-3 placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors";

export default function SessionsAdmin({ http, withRefresh }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = useCallback(async () => {
    try {
      const { data } = await withRefresh(() => http.get("/admin/sessions"));
      setList(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load sessions.");
    }
  }, [http, withRefresh]);
  useEffect(() => {
    load();
  }, [load]);

  const body = (s) => ({ ...s, seats: s.seats ? Number(s.seats) : null });
  const add = async (e) => {
    e.preventDefault();
    try {
      await withRefresh(() => http.post("/admin/sessions", body(form)));
      setForm(EMPTY);
      toast.success("Session added — it now shows on the site.");
      load();
    } catch {
      toast.error("Please check the title and date.");
    }
  };
  const toggle = async (s) => {
    await withRefresh(() => http.put(`/admin/sessions/${s.id}`, body({ ...s, active: !s.active })));
    load();
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    await withRefresh(() => http.delete(`/admin/sessions/${s.id}`));
    load();
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="sessions-admin" className="mt-10">
      <form onSubmit={add} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-x-6 gap-y-2 border border-[#D9B47A]/20 p-6">
        <input data-testid="session-title" required maxLength={120} value={form.title} onChange={set("title")} placeholder="Session title" className={`${field} lg:col-span-2`} />
        <input data-testid="session-date" required type="datetime-local" value={form.starts_at} onChange={set("starts_at")} className={`${field} [color-scheme:dark]`} />
        <input data-testid="session-location" maxLength={160} value={form.location} onChange={set("location")} placeholder="Location / online" className={field} />
        <input data-testid="session-seats" type="number" min={1} value={form.seats} onChange={set("seats")} placeholder="Seats (optional)" className={field} />
        <button data-testid="session-add" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-5 py-3 mt-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">
          <Plus className="w-4 h-4" /> Add
        </button>
        <input data-testid="session-notes" maxLength={600} value={form.notes} onChange={set("notes")} placeholder="Short note shown to seekers (optional)" className={`${field} sm:col-span-2 lg:col-span-6`} />
      </form>
      {list?.length === 0 && <p className="mt-10 font-display italic text-2xl text-[#A8998C]">No sessions yet. The site invites seekers to request an invitation.</p>}
      <ul className="mt-6">
        {(list ?? []).map((s) => (
          <li key={s.id} data-testid={`session-row-${s.id}`} className={`flex flex-wrap items-center gap-4 justify-between border-b border-[#D9B47A]/15 py-5 ${s.active ? "" : "opacity-50"}`}>
            <div>
              <p className="font-display text-xl">{s.title}</p>
              <p className="text-sm text-[#A8998C] mt-1">{new Date(s.starts_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}{s.location ? ` · ${s.location}` : ""}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-tt-mono text-[12px] text-[#D9B47A]">{s.requested} requested{s.seats ? ` / ${s.seats} seats` : ""}</span>
              <button data-testid={`session-toggle-${s.id}`} onClick={() => toggle(s)} title={s.active ? "Hide from site" : "Show on site"} className="p-2 text-[#CFC2B6] hover:text-[#D9B47A]">
                {s.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button data-testid={`session-delete-${s.id}`} onClick={() => remove(s)} className="p-2 text-[#CFC2B6] hover:text-[#E8838E]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
